import styled from '@emotion/styled';
import { ifProp, switchProp } from 'styled-tools';
import { motion } from 'framer-motion';
import Logo from '~/icons/logo.svg';
import { Link, Button } from '~/shared/components';
import { Container } from '~/shared/components/Container';
import { StyledInput } from '~/shared/components/form/components/controllable-elements/input-element/styled';
import { mq } from '~/lib';
import { theme } from '~/theme';
import { breakpoints } from '~/theme/breakpoints';

type StyledIconButtonProps = {
    mobileOnly?: boolean;
    onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
    onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>;
    full?: boolean;
    desktop?: boolean;
    textColor?: string;
    reverseIcon?: boolean;
    checkout?: boolean;
    mobile?: boolean;
    disabled?: boolean;
};

export const StyledIconButton = styled(Link)<StyledIconButtonProps>(
    ({ theme }) => ({
        display: 'flex',
        padding: `${theme.space[4]} ${theme.space[2]}`,
        position: 'relative',
        '&:hover': {
            backgroundColor: theme.colors.grey30,
            color: 'inherit',
            textDecoration: 'inherit',
        },
        svg: {
            height: 24,
            width: 24,
        },
        alignItems: 'center',
    }),
    ifProp('mobileOnly', {
        svg: {
            height: 16,
            width: 16,
        },
        [breakpoints.sm]: {
            display: 'none',
        },
    }),
);

export const StyledSmallIconButton = styled(StyledIconButton)<StyledIconButtonProps>(
    ({ theme }) => ({
        flexDirection: 'column',
        margin: `unset`,
        padding: `${theme.space[1]}`,
        '&:hover': {
            backgroundColor: 'transparent',
        },
        svg: {
            width: `${theme.metrics.xsmall}`,
            height: `${theme.metrics.xsmall}`,
            margin: 'unset',
        },
        [breakpoints.xs]: {
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 'unset',
            svg: {
                height: 16,
                width: 16,
            },
        },
        '&:focus-visible': {
            boxShadow: theme.shadows.accessibility,
        },
    }),
    ifProp('textColor', ({ textColor }) => ({
        color: textColor,
    })),
);
export const StyledSmallIconButtonUsp = styled.div<StyledIconButtonProps>(
    ({ theme }) => ({
        padding: `${theme.space[1]} ${theme.space[2]} ${theme.space[1]} ${theme.space[2]}`,
        margin: `${theme.space[1]}`,
        flexDirection: 'row',
        display: 'flex',
        '&:hover': {
            backgroundColor: 'unset',
            textDecoration: 'none',
        },
        svg: {
            width: 24,
            height: 24,
        },
    }),
    ifProp('textColor', ({ textColor }) => ({
        color: textColor,
        svg: {
            '& path': {
                fill: textColor,
            },
        },
    })),
);

export const StyledIconButtonLogin = styled(StyledSmallIconButton)<StyledIconButtonProps>(
    () => ({
        [breakpoints.xs]: {
            display: 'flex',
        },
        [breakpoints.sm]: {
            display: 'flex',
        },
        [breakpoints.md]: {
            display: 'inherit',
        },
    }),
    ifProp('checkout', () => ({
        marginLeft: 'auto',
        padding: 0,

        [mq(0, 'md')]: {
            display: 'none',
        },
    })),
    switchProp('disabled', {
        true: () => ({
            opacity: 0.7,
            cursor: 'default',
        }),
    }),
);

export const StyledIconBadge = styled(motion.span)(({ theme }) => ({
    ...theme.mixins.useTextStyle('caption'),
    borderRadius: '50%',
    position: 'absolute',
    height: '20px',
    width: '20px',
    top: '-6px',
    right: '-2px',
    backgroundColor: theme.colors.brandOrangeMedium,
    color: theme.colors.white,
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    alignItems: 'center',
}));

export const StyledContent = styled(Container)<{
    topBanner?: boolean;
    desktop?: boolean;
}>(
    () => ({
        display: 'flex',
    }),
    ifProp('topBanner', ({ theme }) => ({
        padding: 0,
        maxWidth: 'unset',
        [breakpoints.xs]: {
            marginTop: theme.space[2],
            padding: `${theme.space[2]} 0 0 0`,
        },
    })),
);

export const StyledBannerMotion = styled(Container)<{
    isHidden: boolean;
}>(
    () => ({
        position: 'sticky',
        opacity: 1,
        maxWidth: '100%',
        overflow: 'hidden',
        transition: `opacity 0.5s ease,`,
    }),
    ifProp('isHidden', () => ({
        opacity: 0,
        display: 'none',
    })),
);

export const StyledMainNavigation = styled(motion.header)(({ theme }) => ({
    top: 0,
    transition: '1s',
    position: 'sticky',
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    borderBottom: `1px solid ${theme?.colors.headerBottomBorder}`,
    backgroundColor: theme.colors.headerColor,
    zIndex: theme.zIndices[2],
    [breakpoints.xs]: {
        position: 'sticky',
        padding: 0,
    },
    ':focus-visible': {
        outline: 'none',
    },
}));

export const MenuHeaderContainer = styled(StyledContent)(({ theme }) => ({
    position: 'relative',
    padding: `${theme.space[4]} ${theme.space[5]} 18px ${theme.space[5]}`,
    justifyContent: 'space-between',
    background: `${theme.colors.white}`,
    [mq(0, 'md')]: {
        padding: `${theme.space[3]} ${theme.space[4]}`,
    },

    [mq(0, 'sm')]: {
        padding: `${theme.space[2]} ${theme.space[3]}`,
    },
}));

export const StyledNav = styled.nav({
    display: 'flex',
});

export const StyledDesktopNav = styled(Container)(({ theme }) => ({
    display: 'none',
    padding: `${theme.space[3]} ${theme.space[5]} ${theme.space[3]}  ${theme.space[5]}`,
    width: 'unset',
    [breakpoints.md]: {
        display: 'inherit',
    },
}));

export const StyledIconNav = styled(StyledNav)<{
    notMobile?: boolean;
    mobile?: boolean;
    justifyContent?: string;
}>(
    () => ({
        display: 'flex',
        flexGrow: 1,
        flexBasis: 0,
        alignContent: 'center',
        alignItems: 'flex-end',
        justifyContent: 'center',
        flexDirection: 'row',
    }),
    ifProp('mobile', () => ({
        display: 'none',
        [breakpoints.sm]: {
            display: 'flex',
            alignItems: 'center',
        },
        [breakpoints.xs]: {
            display: 'flex',
            alignItems: 'center',
            svg: {
                height: 16,
                width: 16,
            },
        },
        [breakpoints.md]: {
            display: 'none',
        },
    })),
    ifProp('justifyContent', ({ justifyContent }) => ({
        justifyContent: justifyContent,
        gap: theme.space[2],
        [breakpoints.md]: {
            flexDirection: 'row',
        },
    })),
    ifProp('notMobile', () => ({
        display: 'none',
        [breakpoints.md]: {
            display: 'flex',
        },
    })),
);

export const StyledLeftContainer = styled.div({
    display: 'flex',
    flexBasis: 0,
    flexGrow: 1,
    zIndex: 200,
});

export const StyledRightContainer = styled.div({
    display: 'flex',
    flexBasis: 0,
    flexGrow: 1,
    zIndex: 200,
});

export const StyledLogoContainer = styled.div<{ activeOverlay: boolean }>(
    () => ({
        display: 'flex',
        flexGrow: 0,
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
        [breakpoints.xs]: {
            padding: `0 ${theme.space[1]} ${theme.space[1]} ${theme.space[1]}`,
        },
        [breakpoints.md]: {
            padding: 0,
        },
    }),
    ifProp('activeOverlay', () => ({
        [breakpoints.sm]: {
            opacity: 0,
            position: 'absolute',
        },
    })),
);

export const StyledLogo = styled(Logo)(({ theme }) => ({
    display: 'flex',
    height: theme.space[5],
    width: 253,
    [mq(0, 'xs')]: {
        height: 11,
        width: 75,
    },
    [mq('xs', 'md')]: {
        height: 18,
        width: 150,
    },
}));
export const StyledHeaderOptionText = styled.p(({ theme }) => ({
    color: theme.colors.headerTextColor,
    ...theme.mixins.useTextStyle('bodySmall'),
    [breakpoints.xs]: {
        fontSize: theme.fontSizes['2xs'],
    },
}));

export const StyledHeaderSmallIconOptionText = styled(StyledHeaderOptionText)<{
    desktop?: boolean;
    checkout?: boolean;
    textColor?: string;
    metaMenu?: boolean;
}>(
    () => ({
        fontSize: theme.fontSizes['2xs'],
        whiteSpace: 'nowrap',
        [breakpoints.xs]: {
            display: 'flex',
        },
        '&:hover': {
            textDecoration: 'none',
        },
    }),
    ifProp('desktop', ({ theme }) => ({
        marginLeft: theme.space[3],
    })),
    ifProp('checkout', ({ theme }) => ({
        marginLeft: theme.space[2],
        fontSize: theme.fontSizes['xs'],
        fontWeight: theme.fontWeights.medium,
    })),
    ifProp('textColor', ({ textColor }) => ({
        color: textColor,
    })),
    ifProp('metaMenu', () => ({
        marginLeft: 0,
    })),
);
export const StyledHeaderSmallIconOptionTextUsp = styled(StyledHeaderOptionText)<{
    desktop?: boolean;
    textColor?: string;
    metaMenu?: boolean;
}>(
    () => ({
        marginLeft: theme.space[3],
        [breakpoints.xs]: {
            display: 'flex',
        },
        '&:hover': {
            textDecoration: 'none',
        },
    }),

    ifProp('textColor', ({ textColor }) => ({
        color: textColor,
    })),
);

export const StyledSearchInputMobile = styled(StyledInput)(({ theme }) => ({
    color: theme.colors.headerTextColor,
    textAlign: 'center',
    width: 'unset',
    padding: `0 0 0 42px`,
    backgroundColor: theme.colors.headerColor,
    '::placeholder': {
        textAlign: 'left',
    },
    height: 30,
    [mq('sm')]: {
        height: 40,
    },
}));

export const StyledSearchInputContainer = styled(Container)<{
    notMobile?: boolean;
    activeOverlay?: boolean;
}>(
    ({ theme }) => ({
        position: 'relative',
        display: 'none',
        alignItems: 'center',
        boxSizing: 'border-box',
        width: 'unset',
        opacity: 1,
        justifyContent: 'center',
        border: `1px solid ${theme.colors.black}`,
        borderRadius: theme.space[1],
        backgroundColor: theme.colors.headerColor,
        margin: `${theme.space[2]} ${theme.space[3]} ${theme.space[2]} ${theme.space[3]}`,
        height: '32px',
        zIndex: 200,
        ':has(:focus-visible)': {
            boxShadow: theme.shadows.accessibilityTextField,
        },

        [breakpoints.sm]: {
            display: 'flex',
            height: '42px',
        },
    }),
    ifProp('notMobile', () => ({
        [breakpoints.sm]: {
            flexGrow: 1,
            flexBasis: 0,
            margin: 0,
            // Scales based on window width up with a minimum value of 252px.
            maxWidth: 'max(252px, 17vw)',
        },
    })),
    ifProp('activeOverlay', () => ({
        [breakpoints.md]: {
            maxWidth: '690px',
            transition: 'max-width 0.5s ease-in-out',
        },
    })),
);

export const SearchInputMobileWrapper = styled.div({
    background: theme.colors.white,
    width: '100%',
    padding: `${theme.space[2]} ${theme.space[3]}`,
});

export const StyledSearchInputContainerMobile = styled(StyledSearchInputContainer)<{
    notMobile?: boolean;
    activeOverlay?: boolean;
}>(
    () => ({
        display: 'flex',
        margin: 0,
        background: 'transparent',

        [breakpoints.md]: {
            display: 'none',
        },

        [mq(0, 'sm')]: {
            padding: `12px ${theme.space[3]}`,
        },
    }),
    ifProp('notMobile', () => ({
        [breakpoints.sm]: {
            display: 'flex',
            flexGrow: 1,
            flexBasis: 0,
            padding: 0,
            maxWidth: `calc(min(25vw, ${theme.sizes.pageMaxWidth / 4}px) - ${theme.space[6]})`,
        },
        [breakpoints.xs]: {
            display: 'none',
        },
    })),
    ifProp('activeOverlay', () => ({
        [breakpoints.md]: {
            maxWidth: '690px',
            transition: 'max-width 0.5s ease-in-out',
        },
    })),
);

export const StyledMirrorSearchContainer = styled(StyledSearchInputContainer)<{
    notMobile?: boolean;
    activeOverlay?: boolean;
}>(
    () => ({
        display: 'flex',
        margin: 0,
        background: 'transparent',
        justifyContent: 'flex-start',

        [breakpoints.md]: {
            display: 'none',
        },

        [mq(0, 'sm')]: {
            padding: `12px ${theme.space[3]}`,
        },
    }),
    ifProp('notMobile', () => ({
        [breakpoints.sm]: {
            display: 'flex',
            flexGrow: 1,
            flexBasis: 0,
            padding: 0,
            maxWidth: `calc(min(25vw, ${theme.sizes.pageMaxWidth / 4}px) - ${theme.space[6]})`,
        },
        [breakpoints.xs]: {
            display: 'none',
        },
    })),
    ifProp('activeOverlay', () => ({
        [breakpoints.md]: {
            maxWidth: '690px',
            transition: 'max-width 0.5s ease-in-out',
        },
    })),
);

export const StyledSearchInputDesktop = styled(StyledSearchInputMobile)(() => ({
    // Setting fontSize to 16 prevents auto-zoom on IOS
    fontSize: 16,
    textAlign: 'left',
    width: '100%',
    '::placeholder': {
        textAlign: 'center',
    },
    '::-webkit-search-cancel-button': {
        display: 'none',
    },
    '::-webkit-search-decoration': {
        display: 'none',
    },
}));

export const StyledSearchCloseButton = styled(Button)(({ theme }) => ({
    paddingLeft: theme.space[3],
    paddingRight: theme.space[3],
    height: 32,
    minHeight: 32,
    svg: {
        height: '11px',
        width: '11px',
    },
    [mq('sm')]: {
        height: 42,
        minHeight: 42,
    },
}));

export const SearchPlaceholder = styled.div<{ isShowSearch?: boolean }>(
    ({ theme }) => ({
        pointerEvents: 'none',
        position: 'absolute',
        color: theme.colors.black,
        ...theme.mixins.useTextStyle('searchPlaceHolder'),
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        left: 12,
        zIndex: 1,
        svg: {
            height: 20,
            width: 20,
        },
    }),
    ifProp('isShowSearch', () => ({
        pointerEvents: 'auto',
        ':focus-visible': {
            boxShadow: theme.shadows.accessibility,
        },
    })),
);

export const ProgressLoader = styled.div({
    position: 'relative',
    height: '2px',
    marginTop: '-2px',
    width: '100%',
    zIndex: 0,
});

export const SearchPlaceholderIconContainer = styled.div({
    zIndex: 1,
    display: 'flex',
    cursor: 'pointer',
    backgroundColor: theme.colors.white,
    color: theme.colors.grey150,
});

export const SearchPlaceholderTextContainer = styled.div({
    pointerEvents: 'none',
    touchAction: 'none',
    zIndex: 1,
    display: 'flex',
    backgroundColor: theme.colors.white,
    color: theme.colors.grey200,
});

export const StyledSearchStarsBox = styled.button(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    height: 32,
    borderRadius: theme.space[1],
    border: 0,
    paddingBlock: theme.space[2],
    paddingInline: '34px 12px',
    appearance: 'none',
    color: theme.colors.white,
    backgroundColor: theme.colors.black,
    backgroundImage: 'url("/assets/Imerco_stars_no_bg.gif")',
    backgroundPosition: '2px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '30px',
    flexShrink: 0,
    fontSize: theme.fontSizes['2xs'],
    fontWeight: theme.fontWeights.medium,
    fontFamily: theme.fonts.roboto,
    marginRight: theme.space[2],
    cursor: 'pointer',
    ':focus-visible': {
        boxShadow: theme.shadows.accessibility,
    },
    [mq('sm')]: {
        height: 42,
        fontSize: theme.fontSizes.xs,
    },
}));

export const StyledSearchWithStarsWrapper = styled.div(() => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    flexGrow: 1,
    [mq(0, 'md')]: {
        [`& ${StyledSearchInputContainerMobile}`]: {
            width: '100%',
        },
    },
}));

export const StyledSearchStarsHint = styled.div(({ theme }) => ({
    position: 'absolute',
    top: 'calc(100% + 10px)',
    left: 0,
    zIndex: theme.zIndices[3],
    border: `1px solid ${theme.colors.headerSearchInputNewColor}`,
    borderRadius: theme.space[1],
    backgroundColor: theme.colors.white,
    color: theme.colors.black,
    padding: `${theme.space[2]} ${theme.space[2]}`,
    whiteSpace: 'nowrap',
    boxShadow: `inset 0 1px 0 0 ${theme.colors.white}`,
    ...theme.mixins.useTextStyle('caption'),
    '::before': {
        content: '""',
        position: 'absolute',
        top: '-7px',
        left: '15px',
        width: 0,
        height: 0,
        borderLeft: '7px solid transparent',
        borderRight: '7px solid transparent',
        borderBottom: `7px solid ${theme.colors.headerSearchInputNewColor}`,
    },
    '::after': {
        content: '""',
        position: 'absolute',
        top: '-6px',
        left: '16px',
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderBottom: `6px solid ${theme.colors.white}`,
    },
}));
